import React, { useRef, useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTheme } from '@material-ui/core';
import currency from 'currency.js';
import Skeleton from '@material-ui/lab/Skeleton';
import { Alert as MuiAlert } from '@material-ui/lab';
import * as Mui from '@material-ui/core';
import { Divider }  from '@mui/material';
import * as MuiIcons from '@material-ui/icons';

import { debounce } from 'lodash';
import { formatCurrency } from '~/utils/format';
import { SET_FOCUS_CONSULTA_PRODUTO, SET_FOCUS_QUANTIDADE } from '~/store/modules/pdv/pdv-constants';
import { TIPO_DOCUMENTO, API_BASE_URL } from '~/constants';
import * as pdvActions from '~/store/modules/pdv/pdv-actions';

import { CurrencyInputCustom } from '~/components/CustomInput';
import ModalCondicaoPagamentoProduto from '~/pages/TelaVenda/modal/ModalCondicaoPagamentoProduto';
import ModalProdutoSimilar from '~/pages/TelaVenda/modal/ModalProdutoSimilar';
import ModalVendaCasada from '~/pages/TelaVenda/modal/ModalVendaCasada';
import NotFound from '~/assets/not-found.png';
import QuantidadeInput from '~/components/QuantidadeInput';
import ModalEnderecos from '~/pages/TelaVenda/modal/ModalEnderecos';
import { selectorRotina } from '~/store/modules/auth/auth-selectors';
import { Tooltip } from '@material-ui/core';
import toast from '~/services/toast';
import { selectorParametroAsBoolean } from '~/store/modules/auth/auth-selectors';
import api from '~/services/api';
import InfoPopover from '~/components/InfoPopover'

const ConsultaProduto = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const quantidadeRef = useRef();

  const permissoes = useSelector(state => state?.auth?.permissoes ?? []);
  const { loadingProduto, itemVenda, venda, tipoDocumento, setFocusInputQuantidade } = useSelector(
    state => state.pdv
  );
  const ambientes = useSelector(state => state?.pdv?.selects?.ambientes ?? []);
  const [showModalCondicaoPagamento, setShowModalCondicaoPagamento] = useState(false);
  const [showModalProdutoSimilar, setShowModalProdutoSimilar] = useState(false);
  const [showModalEnderecos, setShowModalEnderecos] = useState(false);
  const [showModalVendaCasada, setShowModalVendaCasada] = useState(false);
  const [editarValorUnitario, setEditarValorUnitario] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [valorOriginalProduto, setValorOriginalProduto] = useState(
    itemVenda?.produtoEstProduto?.estProdutoImposto[0]?.precoVenda
  );
  const [armazens, setArmazens] = useState([]);
  const [loadingArmazem, setLoadingArmazem] = useState(false);
  const [produtoComplemento, setProdutoComplemento] = useState('');

  const bloqueiaDesconto = useSelector(selectorRotina('F060FAT')); // Bloquear Desconto e Acréscimo por Item
  const parametroPF054 = useSelector(selectorParametroAsBoolean('PF054')) // Bloquear descontos em itens com preço inferior ao preço de cadastro
  const parametroPC009 = useSelector(selectorParametroAsBoolean('PC009')) // Habilitar Controle de Ambientes
  const rotinaF140DSA = useSelector(selectorRotina('F140DSA')) // Permita ao usuário editar despesas no documento de saída
  const rotinaF070FAT = useSelector(selectorRotina('F070FAT')) // Habilitar a exibição do campo complementar na tela de vendas

  const valorMercadoria = useMemo(() => {
    return currency(
      itemVenda?.itemValorUnitario * itemVenda?.itemQuantidade
    );
  }, [itemVenda?.itemValorUnitario, itemVenda?.itemQuantidade]);

  const valorDescontoAcrescimo = useMemo(() => {
    return currency(
      (itemVenda?.itemValorAcrescimo || 0) -
      (itemVenda?.itemValorDesconto || 0)
    );
  }, [
    itemVenda?.itemValorAcrescimo,
    itemVenda?.itemValorDesconto
  ]);

  const valorTotal = useMemo(() => {
    return currency(
      (itemVenda?.itemValorUnitario * itemVenda?.itemQuantidade) -
      (itemVenda?.itemValorDesconto || 0) +
      (itemVenda?.itemValorAcrescimo || 0)
    );
  }, [
    itemVenda?.itemValorUnitario,
    itemVenda?.itemQuantidade,
    itemVenda?.itemValorDesconto,
    itemVenda?.itemValorAcrescimo
  ]);

  useEffect(() => {
    if (itemVenda?.produtoEstProduto?.armazens) {
      fetchArmazem();
    } else {
      setArmazens([]);
    }
  }, [itemVenda?.produtoEstProduto?.armazens]);

  const handleClick = event => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const changeUnidadeComercial = e => {
    const unidade = e.target.value;
    dispatch(pdvActions.alterarUnidadeComercial(unidade));
  };

  const changeQuantidade = quantidade => {
    const novoItem = {
      ...itemVenda,
      itemQuantidade: quantidade
    };
    dispatch(pdvActions.atualizarQuantidade(novoItem, quantidade));
  };

  const montarItemVenda = itemVenda => {
    const itemQuantidade = Number(itemVenda.itemQuantidade);

    const itemValorUnitario = Number(currency(itemVenda?.itemValorUnitario, { precision: 2 }));

    const itemValorTotal = Number(
      currency(itemValorUnitario * itemQuantidade, { precision: 2 })
        .add(itemVenda?.itemValorAcrescimo ?? 0)
        .add(itemVenda?.impostoIcmsSt ?? 0)
        .add(itemVenda?.impostoIpi ?? 0)
        .subtract(itemVenda?.itemValorDesconto ?? 0)
    );

    const quantidadexembalagem = Number(
      currency(itemVenda.produtoQuantidadeEmbalagem * itemVenda.itemQuantidade, {
        precision: 2
      })
    );
    return {
      ...itemVenda,
      itemQuantidade,
      quantidadexembalagem,
      itemValorUnitario,
      itemValorTotal
    };
  };

  const updateQuantidade = quantidade => {
    const produto = itemVenda?.produtoEstProduto;
    const quantidadeCalculada = quantidade / produto?.embalagemQuantidade;

    if (quantidadeRef.current && quantidadeRef.current.setValue) {
      quantidadeRef.current.setValue(quantidade);
    }
    itemVenda.itemQuantidade = quantidade;
    itemVenda.quantidadexembalagem = quantidadeCalculada;
  };

  const changeValorUnitario = e => {
    const precoVenda = e.floatvalue;

    itemVenda.itemValorPrecoVendaOriginal = valorOriginalProduto
    if (precoVenda < valorOriginalProduto) {
      itemVenda.edicaoManual = true;

      if (parametroPF054) {
        itemVenda.itemValorDesconto = 0;
        itemVenda.itemPercentualDesconto = 0;
      }
    } else if (precoVenda > valorOriginalProduto) {
      itemVenda.edicaoManual = true;
    } else {
      itemVenda.edicaoManual = false;
    }
    // itemVenda.edicaoManual = true;
    dispatch(pdvActions.atualizarPrecoVendaProduto(itemVenda, precoVenda));
  };

  const changeDesconto = e => {
    const desconto = e.floatvalue;
    dispatch(pdvActions.atualizarDescontoItem(itemVenda, desconto));
  };

  const changeDescontoPercentual = e => {
    const desconto = e.floatvalue;
    dispatch(pdvActions.atualizarDescontoItemPercentual(itemVenda, desconto));
  };

  const changeAcrescimo = e => {
    const acrescimo = e.floatvalue;
    dispatch(pdvActions.atualizarAcrescimoItem(itemVenda, acrescimo));
  };

  useEffect(() => {
    const descricaoBase = itemVenda?.produtoEstProduto?.descricao;
    const descricaoCompleta = itemVenda?.produtoDescricao;

    if (!descricaoBase || !descricaoCompleta) {
      setProdutoComplemento('');
      return;
    }

    const prefixo = `${descricaoBase} - `;

    if (descricaoCompleta.startsWith(prefixo)) {
      setProdutoComplemento(descricaoCompleta.substring(prefixo.length));
    } else {
      setProdutoComplemento('');
    }
  }, [itemVenda]);

  const debouncedAtualizarProdutoDescricaoComplemento = useMemo(
    () =>
      debounce((itemVenda, produtoDescricao) => {
        dispatch(
          pdvActions.atualizarProdutoDescricao(
            itemVenda,
            produtoDescricao
          )
        );
      }, 1000),
    [dispatch]
  );

  useEffect(() => {
    return () => {
      debouncedAtualizarProdutoDescricaoComplemento.cancel();
    };
  }, [debouncedAtualizarProdutoDescricaoComplemento]);

  const handleProdutoComplementoBlur = () => {
    debouncedAtualizarProdutoDescricaoComplemento.flush();
  };

  const changeProdutoComplemento = e => {
    const descricaoBase = itemVenda?.produtoEstProduto?.descricao ?? '';

    const tamanhoReservado = descricaoBase.length + (descricaoBase ? 3 : 0);
    const tamanhoMaximoComplemento = Math.max(0, 120 - tamanhoReservado);

    var complemento = e.target.value.toUpperCase();
    if (complemento.length > tamanhoMaximoComplemento) {
      return;
    }
    complemento = complemento.slice(0, tamanhoMaximoComplemento);
    setProdutoComplemento(complemento);

    const produtoDescricao = complemento?.trim() ? `${descricaoBase} - ${complemento}` : descricaoBase;

    debouncedAtualizarProdutoDescricaoComplemento(
      itemVenda,
      produtoDescricao
    );
  };

  const showToastIfDifferent = (produto, originalQuantidade, novaQuantidade) => {
    if (originalQuantidade !== novaQuantidade) {
      toast.produtoMulti(
        `<b>${produto?.codigo} - ${produto?.descricao}</b> é múltiplo de embalagem ${produto?.embalagemQuantidade},
        e a quantidade vendida foi alterada de ${originalQuantidade} para ${novaQuantidade}`
      );
    }
  };

  const handleEmbalagemMultiplo = (produto, quantidade) => {
    const quantidadeEmbalagem = produto?.embalagemQuantidade;
    const quantidadeRefValue = parseFloat(quantidade);

    if (quantidadeEmbalagem > 0 && quantidadeRefValue !== 0) {
      const resto = quantidadeRefValue % quantidadeEmbalagem;
      let quantidadeAjustada;

      if (resto >= quantidadeEmbalagem / 2) {
        quantidadeAjustada = quantidadeRefValue + (quantidadeEmbalagem - resto);
      } else {
        quantidadeAjustada = quantidadeRefValue - resto;
      }

      quantidadeAjustada = parseFloat(quantidadeAjustada.toFixed(2));

      if (quantidadeRef.current) {
        quantidadeRef.current.value = quantidadeAjustada;
      }

      showToastIfDifferent(produto, quantidadeRefValue, quantidadeAjustada);
      return montarItemVenda({
        ...itemVenda,
        itemQuantidade: quantidadeAjustada
      })
    }
  };

  const handleFracionavel = (quantidade) => {
    const valorFormatado = quantidade.replace(',', '.');
    const valorNumerico = parseFloat(valorFormatado);

    if (!isNaN(valorNumerico)) {
      return montarItemVenda({
        ...itemVenda,
        itemQuantidade: valorNumerico
      });
    }
  };

  const handleDefault = (quantidade) => {
    const regex = /^[0-9\b]+$/;
    if (quantidade === '' || regex.test(quantidade)) {
      const valorFormatado = quantidade.replace(',', '.');
      const valorNumerico = parseFloat(valorFormatado);

      if (!isNaN(valorNumerico)) {
        return montarItemVenda({
          ...itemVenda,
          itemQuantidade: valorNumerico
        })
      }
    }
  };

  const validaQuantidade = itemVenda => {
    const produto = itemVenda?.produtoEstProduto;
    let quantidade = String(itemVenda?.itemQuantidade) || '0';

    if (isNaN(quantidade)) {
      quantidade = '0'
    }
    if (quantidadeRef.current) {
      quantidadeRef.current.value = quantidade;
    }

    let itemValidado = itemVenda
    if (produto?.embalagemMultiplo) {
      itemValidado = handleEmbalagemMultiplo(produto, quantidade);
    } else if (produto?.fracionavel) {
      itemValidado = handleFracionavel(quantidade);
    } else {
      itemValidado = handleDefault(quantidade);
    }
    return itemValidado
  }

  const keyDownQuantidade = ev => {
    if (ev.key === 'Enter') {
      ev.preventDefault();
      const quantidade = Number(itemVenda?.itemQuantidade);
      if (!quantidade || quantidade <= 0) {
        toast.warning("Informe uma quantidade válida");
        return;
      }

      const itemMontado = validaQuantidade(itemVenda);
      handleAdicionarProduto(itemMontado);
    }
  };

  const keyDownHandler = ev => {
    if (ev.key === 'Enter') {
      ev.preventDefault();
      const quantidade = Number(itemVenda?.itemQuantidade);
      if (!quantidade || quantidade <= 0) {
        toast.warning("Informe uma quantidade válida");
        return;
      }
      handleAdicionarProduto(itemVenda);
    }
  };

  const handleAdicionarProduto = async venda => {
    const vendaFinal = (venda && venda.itemQuantidade !== undefined) ? venda : itemVenda
    const quantidade = Number(vendaFinal?.itemQuantidade);

    if (!quantidade || quantidade <= 0) {
      toast.warning("Informe uma quantidade válida");
      return;
    }

    const editado = venda?.itens?.find(x => x.uuid === vendaFinal?.uuid);
    if (editado) vendaFinal.editado = true;
    if (venda?.parcelas?.length > 0) {
      const { isConfirmed } = await toast.confirm(
        'Deseja alterar a forma de efetuar os pagamentos?',
        'Ao adicionar um produto, irá remover os pagamentos anteriores',
        {
          icon: 'question',
          confirmButtonText: 'Confirmar',
          cancelButtonText: 'Cancelar'
        }
      );

      if (isConfirmed) {
        dispatch(pdvActions.limparPagamentos());
        dispatch(pdvActions.selecionarMeioPagamento(null));
        dispatch(pdvActions.selecionarCondicaoPagamento(null));
        dispatch(pdvActions.setMultiplosPagamentos(false));
        dispatch(
          pdvActions.adicionarItem(vendaFinal, () => {
            dispatch({ type: SET_FOCUS_QUANTIDADE, focus: false });
            dispatch({ type: SET_FOCUS_CONSULTA_PRODUTO, focus: true });
          })
        );
      }
    } else {
      dispatch(
        pdvActions.adicionarItem(vendaFinal, () => {
          dispatch({ type: SET_FOCUS_QUANTIDADE, focus: false });
          dispatch({ type: SET_FOCUS_CONSULTA_PRODUTO, focus: true });
        })
      );
    }
  };

  useEffect(() => {
    if (setFocusInputQuantidade) {
      quantidadeRef?.current?.focus();
      quantidadeRef?.current?.select();
      dispatch({ type: SET_FOCUS_QUANTIDADE, focus: false });
    }
  }, [setFocusInputQuantidade]);

  useEffect(() => {
    const _permissao = permissoes.find(
      x => 
        (tipoDocumento === TIPO_DOCUMENTO.PEDIDO && x.pctRotinaRotina === 'F120PED') ||
        (tipoDocumento === TIPO_DOCUMENTO.ORCAMENTO && x.pctRotinaRotina === 'F080ORC') ||
        (tipoDocumento === TIPO_DOCUMENTO.DOCUMENTOSAIDA && x.pctRotinaRotina === 'F120DSA')
    );
    const acesso = _permissao?.acesso ?? false;
    if (acesso) {
      setEditarValorUnitario(true);
    }
  }, [permissoes]);

  let produtoModalregra = itemVenda?.produtoEstProduto?.modalRegra || itemVenda?.modalRegra

  const disabledProdutoComplemento =
    !rotinaF070FAT ||
    (
      (
        tipoDocumento === TIPO_DOCUMENTO.PEDIDO ||
        tipoDocumento === TIPO_DOCUMENTO.DOCUMENTOSAIDA
      ) &&
      (
        itemVenda?.produtoOriginadoDeEdicaoDeQuantidade ||
        itemVenda?.fatOrcamentoId ||
        itemVenda?.fatPedidoItemId ||
        itemVenda?.fatDocumentosaidaId
      )
    );

  const disabledQuantidade = useMemo(() => {
    const produtoModal = itemVenda?.produtoEstProduto?.modalRegra || itemVenda?.modalRegra
    return !itemVenda?.produtoEstProduto || loadingProduto || (parametroPF054 && produtoModal)
  }, [itemVenda, loadingProduto, parametroPF054]);

  const disabledDesconto = useMemo(() => {
    const editado =
      itemVenda?.edicaoManual &&
      !(itemVenda?.itemValorUnitario > itemVenda?.produtoPrecoVenda);

    const produtoModal =
      itemVenda?.produtoEstProduto?.modalRegra ||
      itemVenda?.modalRegra ||
      false;

    return (
      !itemVenda?.produtoEstProduto ||
      loadingProduto ||
      bloqueiaDesconto ||
      (parametroPF054 && editado) ||
      (parametroPF054 && produtoModal) ||
      (tipoDocumento === TIPO_DOCUMENTO.DOCUMENTOSAIDA && !rotinaF140DSA)
    );
  }, [
    itemVenda?.edicaoManual,
    itemVenda?.itemValorUnitario,
    itemVenda?.produtoPrecoVenda,
    itemVenda?.produtoEstProduto,
    itemVenda?.produtoEstProduto?.modalRegra,
    itemVenda?.modalRegra,
    loadingProduto,
    bloqueiaDesconto,
    parametroPF054,
    tipoDocumento,
    rotinaF140DSA
  ]);
  
  const fetchArmazem = async () => {
    setLoadingArmazem(true);
    setArmazens([]);

    if (itemVenda?.produtoEstProduto && itemVenda.armazens && itemVenda.armazens.length > 0) {
      const armazemIds = itemVenda.armazens.map(armazem => armazem.id).filter(id => id != null);

      if (armazemIds.length > 0) {
        const idFilterClause = armazemIds.map(id => `id eq ${id}`).join(' or ');
        const finalFilter = `(${idFilterClause}) and tipo eq 1`;

        try {
          const { data: fetchedArmazens } = await api.get(
            `${API_BASE_URL}/v1/consulta/armazem?$orderby=id&$select=descricao,id&$filter=${encodeURIComponent(finalFilter)}`
          );
          setArmazens(fetchedArmazens);
        } catch (error) {
          console.error("Erro ao buscar armazéns filtrados:", error);
          setArmazens([]);
        }
      }
    }
    setLoadingArmazem(false);
  };

  return (
    <>
      <Mui.Grid item xs={12} style={{ boxShadow: theme.shadows[3], marginBottom: 32 }}>
        <Mui.Grid container item spacing={4} style={{ padding: 16 }}>
          <Mui.Grid item lg={12} md={12} xs={12}>
            <Mui.Box container item style={{ flexWrap: 'none', display: 'flex', flexGrow: 1, maxWidth: '100%', flexDirection: 'row', gap: '8px' }}>
              <Mui.Grid container item md={10} style={{ flexGrow: 1, maxWidth: '100%', flexGrow: 1 }}>
                <Mui.Box item md={12} style={{display: 'flex', flexDirection: 'column', maxWidth: '100%', flexGrow: 1 }}>
                  <Mui.Box style={{overflow: 'hidden', textOverflow: 'ellipsis'}}>
                    {loadingProduto ? (
                      <Skeleton variant="text" width={210} height={18} />
                    ) : (
                      <Mui.Typography variant="body2">{itemVenda?.produtoCodigo}</Mui.Typography>
                    )}
                  </Mui.Box>
                  <Mui.Box style={{overflow: 'hidden', textOverflow: 'ellipsis'}}>
                    {loadingProduto ? (
                      <Skeleton variant="text" height={36} />
                    ) : (
                      <Mui.Typography
                        variant="h3"
                        style={{ textTransform: 'uppercase', fontWeight: 600 }}
                      >
                        {itemVenda?.produtoDescricao ?? 'INFORMAR PRODUTO'}
                      </Mui.Typography>
                    )}
                  </Mui.Box>
                </Mui.Box>
                <Mui.Grid container item md={12} justify="space-between" spacing={2} style={{ maxWidth: '100%', flexGrow: 1 }}>
                  <Mui.Grid item md={3} style={{overflow: 'hidden', minWidth: 'max-content', textOverflow: 'ellipsis', maxWidth: '100%', flexGrow: 1 }}>
                    {loadingProduto ? (
                      <Skeleton variant="text" />
                    ) : (
                      <Mui.Typography variant="caption" noWrap>
                        Cód.auxiliar:
                        <b> {itemVenda?.produtoCodigoAuxiliar ?? 0}</b>
                      </Mui.Typography>
                    )}
                  </Mui.Grid>
                  <Mui.Grid item md={3} style={{overflow: 'hidden', minWidth: 'max-content', textOverflow: 'ellipsis', maxWidth: '100%', flexGrow: 1 }}>
                    {loadingProduto ? (
                      <Skeleton variant="text" />
                    ) : (
                      <Mui.Typography variant="caption" noWrap>
                        Cód.barra:
                        <b> {itemVenda?.produtoEan ?? 0}</b>
                      </Mui.Typography>
                    )}
                  </Mui.Grid>
                  <Mui.Grid item md={3} style={{overflow: 'hidden', minWidth: 'max-content', textOverflow: 'ellipsis', maxWidth: '100%', flexGrow: 1 }}>
                    {loadingProduto ? (
                      <Skeleton variant="text" />
                    ) : (
                      <Mui.Typography variant="caption" noWrap>
                        Cód.fabricante:
                        <b> {itemVenda?.produtoCodigoFabricante ?? 0}</b>
                      </Mui.Typography>
                    )}
                  </Mui.Grid>
                  <Mui.Grid item md={3} style={{overflow: 'hidden', minWidth: 'max-content', textOverflow: 'ellipsis', maxWidth: '100%', flexGrow: 1 }}>
                    {loadingProduto ? (
                      <Skeleton variant="text" />
                    ) : (
                      <Mui.Typography variant="caption" noWrap>
                        Unidade:
                        <b> {itemVenda?.produtoUnidadeComercial} </b>
                      </Mui.Typography>
                    )}
                  </Mui.Grid>
                </Mui.Grid>
              </Mui.Grid>
              <Mui.Grid
                container
                item
                md={2}
                direction="column"
                alignContent="flex-end"
                justify="center"
                style={{ minWidth: '85px', maxWidth: '100%', gap: '4px', flexGrow: 1, display: 'flex', flexDirection: 'column', alignSelf: 'flex-end' }}
              >
                <Mui.Typography variant="caption" align="left">
                  Valor Unitário:
                </Mui.Typography>
                {loadingProduto ? (
                  <Skeleton variant="text" width={80} height={24} />
                ) : (
                  <>
                    {editarValorUnitario ? (
                      <Mui.TextField
                        id="itemValorUnitario"
                        variant="outlined"
                        fullWidth
                        value={itemVenda?.itemValorUnitario ?? 0}
                        onChange={changeValorUnitario}
                        disabled={!itemVenda?.produtoEstProduto || produtoModalregra || loadingProduto}
                        InputProps={{
                          inputComponent: CurrencyInputCustom,
                          style: {
                            height: 44
                          }
                        }}
                      />
                    ) : (
                      <Mui.Typography variant="h4" align="right">
                        {formatCurrency(itemVenda?.itemValorUnitario)}
                      </Mui.Typography>
                    )}
                  </>
                )}
              </Mui.Grid>
            </Mui.Box>

            <Mui.Divider orientation="horizontal" style={{ margin: '16px 0' }} />

            <Mui.Grid container item spacing={2}>
              <Mui.Grid item md={2} xs={12}>
                <Mui.Paper style={{ padding: 8, backgroundColor: '#FFF' }}>
                  {loadingProduto ? (
                    <Skeleton variant="rect" width="100%" height={120} />
                  ) : (
                    <img
                      src={itemVenda?.imageUrl ? itemVenda?.imageUrl : NotFound}
                      alt=""
                      width="100%"
                    />
                  )}
                </Mui.Paper>
              </Mui.Grid>
              <Mui.Grid container item md={10}>
                {itemVenda?.produtoEstProduto?.dicaAplicacao && (
                  <Mui.Grid container item>
                    <MuiAlert severity="info" my={3} style={{ width: '100%', marginBottom: 8 }}>
                      <Mui.Typography variant="caption">Dica de Aplicação:</Mui.Typography>
                      <Mui.Typography>
                        {itemVenda?.produtoEstProduto?.dicaAplicacao ?? ''}
                      </Mui.Typography>
                    </MuiAlert>
                  </Mui.Grid>
                )}

                {rotinaF070FAT && (
                  <Mui.Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '4px',
                      flex: '1 1 100%',
                      marginBottom: '8px',
                    }}
                  >
                    <Mui.Typography variant="caption" align="left" noWrap>
                      Produto Complemento
                    </Mui.Typography>
                    <Mui.TextField
                      variant="outlined"
                      fullWidth
                      value={produtoComplemento ?? ''}
                      onChange={changeProdutoComplemento}
                      onBlur={handleProdutoComplementoBlur}
                      onKeyPress={keyDownHandler}
                      disabled={disabledProdutoComplemento || !itemVenda?.produtoEstProduto || loadingProduto}
                      InputProps={{
                        maxLength: Math.max(
                          0,
                          120 - ((itemVenda?.produtoEstProduto?.descricao?.length ?? 0) + 3)
                        ),
                        style: {
                          height: 44,
                          textTransform: 'uppercase',
                        }
                      }}
                    />
                  </Mui.Box>
                )}

                  <Mui.Box
                    sx={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: '8px',
                    }}
                  >

                      <Mui.Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '4px',
                          flex: '1 1 100px'
                        }}
                      >
                        <Mui.Typography variant="caption" align="left" noWrap>
                          Unid. venda
                        </Mui.Typography>
                        <Mui.Select
                          id="produtoUnidadeComercial"
                          variant="outlined"
                          fullWidth
                          disabled={!itemVenda?.produtoEstProduto || loadingProduto}
                          style={{ height: 44 }}
                          defaultValue={'UN'}
                          value={itemVenda?.produtoUnidadeComercial ?? ''}
                          onChange={changeUnidadeComercial}
                        >
                          {itemVenda?.produtoEstProduto?.listaUnidades?.map(item => (
                            <Mui.MenuItem key={item?.valor} value={item?.valor}>
                              {item?.valor}
                            </Mui.MenuItem>
                          ))}
                        </Mui.Select>
                      </Mui.Box>

                      <Mui.Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '4px',
                          flex: '1 1 100px'
                        }}
                      >
                        <Mui.Typography variant="caption" align="left" noWrap>
                          Quantidade (*)
                        </Mui.Typography>

                        <QuantidadeInput
                          ref={quantidadeRef}
                          produto={itemVenda?.produtoEstProduto}
                          value={itemVenda?.itemQuantidade}
                          onChange={changeQuantidade}
                          onKeyPress={keyDownQuantidade}
                          disabled={disabledQuantidade}
                          autoFocus
                          required
                        />
                      </Mui.Box>

                      <Mui.Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '4px',
                          flex: '1 1 100px'
                        }}
                      >
                        <Mui.Typography variant="caption" align="left" noWrap>
                          Desconto (R$)
                        </Mui.Typography>
                        <Mui.TextField
                          id="itemValorDesconto"
                          variant="outlined"
                          fullWidth
                          value={itemVenda?.itemValorDesconto ?? itemVenda?.itemValorDesconto}
                          onChange={changeDesconto}
                          onKeyPress={keyDownHandler}
                          disabled={disabledDesconto}
                          InputProps={{
                            inputComponent: CurrencyInputCustom,
                            style: { height: 44 }
                          }}
                        />
                      </Mui.Box>

                      <Mui.Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '4px',
                          flex: '1 1 100px'
                        }}
                      >
                        <Mui.Typography variant="caption" align="left" noWrap>
                          Desc. (%)
                        </Mui.Typography>
                        <Mui.TextField
                          id="itemPercentualDesconto"
                          variant="outlined"
                          fullWidth
                          value={itemVenda?.itemPercentualDesconto ?? itemVenda?.itemPercentualDesconto}
                          onChange={changeDescontoPercentual}
                          onKeyPress={keyDownHandler}
                          disabled={disabledDesconto}
                          InputProps={{
                            inputComponent: CurrencyInputCustom,
                            style: { height: 44 }
                          }}
                        />
                      </Mui.Box>

                      <Mui.Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '4px',
                          flex: '1 1 100px'
                        }}
                      >
                        <Mui.Typography variant="caption" align="left" noWrap>
                          Acrésc. (R$)
                        </Mui.Typography>
                        <Mui.TextField
                          variant="outlined"
                          fullWidth
                          value={itemVenda?.itemValorAcrescimo ?? 0}
                          onChange={changeAcrescimo}
                          onKeyPress={keyDownHandler}
                          disabled={!itemVenda?.produtoEstProduto || loadingProduto || bloqueiaDesconto ||
                            (tipoDocumento === TIPO_DOCUMENTO.DOCUMENTOSAIDA ? !rotinaF140DSA : '')
                          }
                          InputProps={{
                            inputComponent: CurrencyInputCustom,
                            style: {
                              height: 44
                            }
                          }}
                        />
                      </Mui.Box>

                  </Mui.Box>

                <Mui.Divider orientation="horizontal" style={{ margin: '16px 8px' }} />
                <Mui.Grid container item style={{ gap: '8px' }}>
                  <Mui.Grid container item xs style={{ minWidth: '100px' }}>
                    <Mui.Grid item xs style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                      <Mui.Typography fullWidth variant="caption" style={{ overflowWrap: 'anywhere', wordBreak: 'break-word' }}>Armazém:</Mui.Typography>
                      {loadingProduto || loadingArmazem ? (
                        <Skeleton variant="rect" xs height={44} />
                      ) : (
                        <>
                          <InfoPopover content={itemVenda?.produtoEstArmazem?.descricao ? itemVenda?.produtoEstArmazem?.descricao : ''}>
                            <Mui.Typography fullWidth component="div" style={{textOverflow: 'ellipsis'}}>
                              {armazens?.length > 0 ? (
                                <Mui.Select
                                  variant="outlined"
                                  disabled={!itemVenda?.produtoEstProduto || loadingProduto || loadingArmazem}
                                  value={itemVenda?.produtoEstArmazemId ?? null}
                                  onChange={e => {
                                    dispatch(
                                      pdvActions.alterarArmazem(
                                        armazens?.find(x => x.id === e.target.value)
                                      )
                                    );
                                  }}
                                  fullWidth
                                  style={{
                                    height: 44,
                                    maxWidth: '300px',
                                  }}
                                  MenuProps={{
                                    PaperProps: {
                                      style: {
                                        maxHeight: '300px',
                                        maxWidth: '300px',
                                        overflowX: 'ellipsis'
                                      }
                                    }
                                  }}
                                  renderValue={(selected) => {
                                    const selectedItem = armazens.find(x => x.id === selected)
                                    return (
                                      <span
                                        style={{
                                          display: 'block',
                                          whiteSpace: 'nowrap',
                                          overflow: 'hidden',
                                          textOverflow: 'ellipsis',
                                        }}
                                      >
                                        {selectedItem ? selectedItem.descricao : 'NÃO SELECIONADO'}
                                      </span>
                                    )
                                  }}
                                >
                                  {armazens?.map((x, index) => (
                                    <Mui.MenuItem key={x.id || index} value={x.id} style={{ whiteSpace: 'normal', overflowWrap: 'break-word', wordBreak: 'break-all' }}>
                                      {x.descricao}
                                    </Mui.MenuItem>
                                  ))}
                                </Mui.Select>
                              ) : (
                                <>
                                  {itemVenda?.produtoEstArmazem
                                    ? `${itemVenda?.produtoEstArmazem?.id}-${itemVenda?.produtoEstArmazem?.descricao}`
                                    : 'NÃO SELECIONADO'}
                                </>
                              )}
                            </Mui.Typography>
                          </InfoPopover>
                        </>
                      )}
                    </Mui.Grid>
                    <Mui.Grid item></Mui.Grid>
                  </Mui.Grid>
                  
                  { parametroPC009 &&
                    <Mui.Grid container item xs style={{ minWidth: '100px' }}>
                      <Mui.Grid item xs style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        <Mui.Box style={{ display: 'flex', flexDirection: 'row', gap: '4px', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Mui.Typography variant="caption" style={{overflow: 'hidden', textOverflow: 'ellipsis'}}>Ambiente:</Mui.Typography>
                          {
                            (itemVenda?.estProdutoAmbiente?.id != null &&
                              !loadingProduto && !loadingArmazem) ? 
                              <Tooltip title="Não selecionar ambiente.">
                                <Mui.Fab
                                aria-label="Não selecionar ambiente."
                                disabled={itemVenda?.estProdutoAmbiente?.id === null}
                                style={{
                                  backgroundColor:
                                    !itemVenda?.produtoEstProduto || loadingProduto
                                      ? theme.palette.background.default
                                      : '#FFF',
                                  boxShadow: theme.shadows[6],
                                  width: '15px',
                                  height: '15px',
                                  minHeight: '15px',
                                  minWidth: '15px',
                                }}
                                onClick={() => {
                                  dispatch(
                                    pdvActions.alterarAmbiente({
                                      id: null,
                                      descricao: 'NENHUM'
                                    })
                                  );
                                }}
                              >
                                <MuiIcons.Close style={{fontSize: '0.8rem'}}/>
                              </Mui.Fab>
                            </Tooltip>

                            : <></>
                          }
                            
                        </Mui.Box>
                        {loadingProduto || loadingArmazem ? (
                          <Skeleton variant="rect" xs height={44} />
                        ) : (
                          <InfoPopover content={itemVenda?.estProdutoAmbiente?.descricao ? itemVenda?.estProdutoAmbiente?.descricao : ''}>
                            <Mui.Typography fullWidth component="div" style={{textOverflow: 'ellipsis'}}>
                              {ambientes?.length > 0 ? (
                                <Mui.Select
                                  variant="outlined"
                                  displayEmpty
                                  disabled={!itemVenda?.produtoEstProduto || loadingProduto || loadingArmazem}
                                  value={itemVenda?.estProdutoAmbiente?.id ?? null}
                                  renderValue={(value) =>
                                    <span
                                      style={{
                                        display: 'block',
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                      }}
                                    >
                                      { value === null ? 'NENHUM' : ambientes.find(a => a.id === value)?.descricao }
                                    </span>
                                  }
                                  onChange={e => {
                                    dispatch(
                                      pdvActions.alterarAmbiente(
                                        ambientes.find(a => a.id === e.target.value) ?? {
                                          id: null,
                                          descricao: 'NENHUM'
                                        }
                                      )
                                    );
                                  }}
                                  fullWidth
                                  style={{
                                    height: 44,
                                    maxWidth: '300px',
                                  }}
                                  MenuProps={{
                                    PaperProps: {
                                      style: {
                                        maxHeight: '300px',
                                        maxWidth: '300px',
                                        overflowX: 'ellipsis'
                                      }
                                    }
                                  }}
                                >
                                  <Mui.MenuItem disabled value={null} style={{ whiteSpace: 'normal', overflowWrap: 'break-word', wordBreak: 'break-all' }}>
                                    NENHUM
                                  </Mui.MenuItem>
                                  {ambientes.map((x, index) => (
                                    <Mui.MenuItem key={x.id || index} value={x.id} style={{ whiteSpace: 'normal', overflowWrap: 'break-word', wordBreak: 'break-all' }}>
                                      {x.descricao}
                                    </Mui.MenuItem>
                                  ))}
                                </Mui.Select>
                              ) : (
                                <>
                                  {itemVenda?.estProdutoAmbiente
                                    ? `${itemVenda?.estProdutoAmbiente?.id}-${itemVenda?.estProdutoAmbiente?.descricao}`
                                    : 'NENHUM'}
                                </>
                              )}
                            </Mui.Typography>
                          </InfoPopover>
                        )}
                      </Mui.Grid>
                      <Mui.Grid item></Mui.Grid>
                    </Mui.Grid>
                  }
                  
                  <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', sm: 'none', md: 'none', lg: 'block', xl: 'block' }}}/>
                  <Mui.Grid
                    container
                    item
                    xs
                    style={{ gap: '4px' }}
                    alignContent="flex-end"
                  >
                    {loadingProduto ? (
                      <Mui.Grid item md={12}>
                        <Skeleton variant="text" style={{ marginBottom: 8 }} />
                        <Skeleton variant="text" style={{ marginBottom: 8 }} />
                        <Skeleton variant="text" style={{ marginBottom: 8 }} />
                      </Mui.Grid>
                    ) : (
                      <Mui.Grid container item style={{ gap: '8px' }}>
                        <Mui.Grid container item justify="space-between" style={{ gap: '2px', alignItems: 'center' }}>
                          <Mui.Typography variant="caption" align="left" style={{ minWidth: '100px' }}>
                            Valor mercadoria:
                          </Mui.Typography>
                          <Mui.Typography align="right">
                            {formatCurrency(valorMercadoria)}
                          </Mui.Typography>
                        </Mui.Grid>

                        <Mui.Grid container item justify="space-between" style={{ gap: '2px', alignItems: 'center' }}>
                          <Mui.Typography variant="caption" align="left" style={{ minWidth: '100px' }}>
                            Desconto/Acréscimo:
                          </Mui.Typography>
                          <Mui.Typography align="right">
                            {formatCurrency(Number(valorDescontoAcrescimo))}
                          </Mui.Typography>
                        </Mui.Grid>

                        <Mui.Grid container item justify="space-between" style={{ gap: '2px', alignItems: 'center' }}>
                          <Mui.Typography variant="caption" align="left" style={{ minWidth: '100px' }}>
                            Total:
                          </Mui.Typography>
                          <Mui.Typography align="right">
                            <b>
                              {formatCurrency(valorTotal)}
                            </b>
                          </Mui.Typography>
                        </Mui.Grid>
                      </Mui.Grid>
                    )}
                  </Mui.Grid>
                </Mui.Grid>
              </Mui.Grid>
            </Mui.Grid>
          </Mui.Grid>
        </Mui.Grid>
      </Mui.Grid>
      <Mui.Grid container justify="center" alignItems="center" style={{ marginTop: -54, paddingLeft: '16px', paddingRight: '16px', gap: '16px' }}>
        <Mui.Fab
          variant="extended"
          style={{
            backgroundColor: 
              !itemVenda?.produtoEstProduto || loadingProduto ? theme.palette.background.default : '#09A848',
            color: !itemVenda?.produtoEstProduto || loadingProduto ? theme.palette.text.disabled : '#FFF',
            boxShadow: theme.shadows[6],
          }}
          disabled={!itemVenda?.produtoEstProduto || loadingProduto}
          onClick={handleAdicionarProduto}
        >
          <MuiIcons.AddCircle />
          <Mui.Typography style={{ marginLeft: '16px' }}>
            {!venda?.itens?.find(x => x.uuid === itemVenda?.uuid) ? 'Adicionar produto' : 'Atualizar produto'}
          </Mui.Typography>
        </Mui.Fab>
        <Tooltip title="Condições de Pagamento">
          <Mui.Fab
            size="small"
            aria-label="Condição pagamento"
            disabled={!itemVenda?.produtoEstProduto || loadingProduto}
            style={{
              backgroundColor:
                !itemVenda?.produtoEstProduto || loadingProduto
                  ? theme.palette.background.default
                  : '#FFF',
              boxShadow: theme.shadows[6]
            }}
            onClick={() => {
              setShowModalCondicaoPagamento(true);
            }}
          >
            <MuiIcons.LocalAtm />
          </Mui.Fab>
        </Tooltip>
        <Mui.Tooltip title="Mais ações">
          <Mui.Fab
            size="small"
            aria-label="Mais ações"
            disabled={!itemVenda?.produtoEstProduto || loadingProduto}
            style={{
              backgroundColor:
                !itemVenda?.produtoEstProduto || loadingProduto
                  ? theme.palette.background.default
                  : '#FFF',
              boxShadow: theme.shadows[6]
            }}
            onClick={handleClick}
          >
            <MuiIcons.MoreVert />
          </Mui.Fab>
        </Mui.Tooltip>

        <Mui.Menu
          id="simple-menu"
          anchorEl={anchorEl}
          keepMounted
          open={Boolean(anchorEl)}
          onClose={handleClose}
        >
          <Mui.MenuItem onClick={() => setShowModalProdutoSimilar(true)}>
            Produto similar
          </Mui.MenuItem>
          <Mui.MenuItem onClick={() => setShowModalVendaCasada(true)}>Venda casada</Mui.MenuItem>
          <Mui.MenuItem onClick={() => setShowModalEnderecos(true)}>Endereços</Mui.MenuItem>
        </Mui.Menu>
      </Mui.Grid>

      {showModalCondicaoPagamento && (
        <ModalCondicaoPagamentoProduto
          open={showModalCondicaoPagamento}
          data={{
            produto: itemVenda?.produtoEstProduto,
            precoVenda: itemVenda?.produtoPrecoVenda
          }}
          onClose={() => {
            setShowModalCondicaoPagamento(false);
          }}
        />
      )}

      {showModalProdutoSimilar && (
        <ModalProdutoSimilar
          open={showModalProdutoSimilar}
          data={{ produto: itemVenda?.produtoEstProduto }}
          onClose={({ estProduto, estProdutoGrade, estProdutoImposto, estArmazem, ...produto }) => {
            setShowModalProdutoSimilar(false);

            dispatch(
              pdvActions.selecionarProduto(
                {
                  ...estProduto,
                  estProdutoGrade,
                  estArmazem,
                  estProdutoImposto
                },
                () => {
                  dispatch({
                    type: SET_FOCUS_QUANTIDADE,
                    focus: true
                  });
                }
              )
            );
          }}
        />
      )}

      {showModalVendaCasada && (
        <ModalVendaCasada
          open={showModalVendaCasada}
          data={{ produto: itemVenda?.produtoEstProduto }}
          onClose={() => {
            setShowModalVendaCasada(false);
          }}
        />
      )}

      {showModalEnderecos && (
        <ModalEnderecos
          open={showModalEnderecos}
          data={{ produto: itemVenda?.produtoEstProduto }}
          armazens={itemVenda.armazens}
          onClose={() => {
            setShowModalEnderecos(false);
          }}
        />
      )}
    </>
  );
};

export default ConsultaProduto;
